import { useLoginMutation } from '@api/api';
import { ContentReference, LoginRequest } from '@api/types';
import {
  AuthBlockContainer,
  Button,
  ForgottenPasswordPageLink,
  RteBlock,
  Title,
} from '@components';
import { Checkbox, FormControlLabel, FormGroup, Stack, TextField } from '@mui/material';
import { useForm } from 'react-hook-form';
import { ResponseError, ResponseErrorList } from '../components/ResponseError';

/**
 * Properties type for the Login Block
 */
export type LoginBlockProps = {
  title?: string;
  text?: string;
  usernameLabel?: string;
  passwordLabel?: string;
  rememberMeLabel?: string;
  submitLabel?: string;
  forgottenPasswordLink?: string;
  redirectPage?: ContentReference;
  redirectDealerPage?: ContentReference;
};

/**
 * Login block component
 */
export const LoginBlock = (props: LoginBlockProps) => {
  const {
    title,
    text,
    usernameLabel,
    passwordLabel,
    rememberMeLabel,
    submitLabel,
    forgottenPasswordLink,
    redirectPage,
    redirectDealerPage,
  } = props;
  const [trigger, request] = useLoginMutation();
  const { register, handleSubmit } = useForm();

  const errors = (request.error as any)?.data?.errors;

  const onSubmit = async (formData: LoginRequest) => {
    const response = await trigger({
      ...formData,
      ReturnUrl: redirectPage?.url ?? '',
      DealerReturnUrl: redirectDealerPage?.url ?? '',
    }).unwrap();

    if (response?.data?.redirectUrl) {
      window.location.href = response?.data?.redirectUrl;
    } else {
      window.location.replace('/');
    }
  };

  return (
    <AuthBlockContainer>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Title variant='h3'>{title}</Title>
          <RteBlock text={text} />
          <TextField {...register('UserName')} name='UserName' label={usernameLabel} fullWidth />
          <ResponseError fieldName='UserName' errors={errors} sx={{ mb: 2 }} />
          <TextField
            {...register('Password')}
            name='Password'
            label={passwordLabel}
            type='password'
            fullWidth
          />
          <ResponseError fieldName='Password' errors={errors} />
          <FormGroup>
            <FormControlLabel
              label={rememberMeLabel}
              control={<Checkbox {...register('RememberMe')} name='RememberMe' />}
            />
          </FormGroup>
          <ForgottenPasswordPageLink>
            <Button variant='text'>{forgottenPasswordLink}</Button>
          </ForgottenPasswordPageLink>
          <ResponseErrorList excludeKeys={['UserName', 'Password']} errors={errors} />
          <Button variant='primary' type='submit' busy={request.isLoading}>
            {submitLabel}
          </Button>
        </Stack>
      </form>
    </AuthBlockContainer>
  );
};
